import { DatePipe, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, PrimeTemplate } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { interval, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ExcelService } from 'src/app/core/services/excel.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { FilesRequest } from 'src/app/models/FilesRequest';
import { IrpfTableService } from 'src/app/shared/components/irpf-table/irpf-table.service';
import { QrCodeDialogComponent } from 'src/app/shared/components/qr-code-dialog/qr-code-dialog.component';
import { STATES_TO_HIDE_CANCEL_OTP_BUTTON, STATES_TO_HIDE_CONTINUE_OTP_BUTTON, SUBSTATES_TO_REOPEN_BELENDER } from 'src/app/shared/constants/common.constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { environment } from 'src/environments/environment';
import { LoadingComponent } from '../../../utils/loading/loading.component';
import { NewInnovationRequestComponent } from '../new-innovation-request/new-innovation-request.component';
import { NewInnovationRequestService } from '../new-innovation-request/new-innovation-request.service';
import { InnovationRequestDetailDocumentsComponent } from './innovation-request-detail-documents/innovation-request-detail-documents.component';
import { InnovationRequestDetailHeaderComponent } from './innovation-request-detail-header/request-detail-header.component';
import { InnovationRequestDetailService } from './innovation-request-detail.service';

@Component({
    selector: 'app-innovation-request-detail',
    templateUrl: './innovation-request-detail.component.html',
    standalone: true,
    imports: [
        NgIf,
        DrawerModule,
        PrimeTemplate,
        LoadingComponent,
        NewInnovationRequestComponent,
        InnovationRequestDetailHeaderComponent,
        InnovationRequestDetailDocumentsComponent,
        RouterOutlet,
        DatePipe,
        TranslateModule,
        TooltipModule,
        QrCodeDialogComponent
    ]
})
export class InnovationRequestDetailComponent implements OnInit, OnDestroy {

    items = [];
    newRequestStatus: {
		title: string, step: number, requestLastStatus?: string
	} = { title: 'New request', step: 1 };

    belenderStatus: {
		actualStatus: string;
		actualStatusMessage: string;
	} = { actualStatus: '', actualStatusMessage: '' };
    consentBelenderUrl: SafeResourceUrl;
    isDownloadZipButtonDisabled = false;

    private restartSyncTimer$ = new Subject<void>();
    private destroy$ = new Subject<void>();

    constructor(
		readonly innovationRequestDetailService: InnovationRequestDetailService,
        private readonly toastService: ToastService,
		private readonly sanitizer: DomSanitizer,
		private readonly translate: TranslateService,
		private readonly confirmationService: ConfirmationService,
        readonly newInnovationRequestService: NewInnovationRequestService,
        readonly utilsService: UtilsService,
        private readonly irpfTableService: IrpfTableService,
        private readonly excelService: ExcelService
    ) { }

    async ngOnInit(): Promise<void> {
        await this.innovationRequestDetailService.getRequestFromUrl();

        this.restartSyncTimer$
            .pipe(
                startWith(void 0),
                switchMap(() => interval(3 * 60 * 1000)),
                takeUntil(this.destroy$)
            )
            .subscribe(async () => await this.sync());
    }

    ngOnDestroy(): void {
        this.irpfTableService.reset();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async sync(): Promise<void> {
        await this.innovationRequestDetailService.refreshRequest(true);
    }

    async syncButton(): Promise<void> {
        await this.sync();
        this.restartSyncTimer$.next();
    }

    reopenOTPWidget(): void {
        try {
            this.innovationRequestDetailService.isLoading = true;
            this.newRequestStatus = {
                title: "Continuar con el OTP",
                step: 3,
                requestLastStatus: this.innovationRequestDetailService.request.personQuery.substatus
            };
            this.belenderStatus = {
                actualStatus: this.innovationRequestDetailService.request.personQuery.substatus,
                actualStatusMessage: this.innovationRequestDetailService.request.personQuery.substatus
            };

            this.consentBelenderUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.innovationRequestDetailService.request.personQuery.consentUrl);
            this.newInnovationRequestService.isFinishingOTP = true;
        } catch (e) {
            console.log(e);
            this.toastService.showError(`Error ${e.status} (${e.statusText})`, `${e.error.detail}`, false);
        } finally {
            this.innovationRequestDetailService.isLoading = false;
        }
    }

    async cancelOTP(): Promise<void> {
        try {
            this.innovationRequestDetailService.isLoading = true;

            const cancelResponse = await this.innovationRequestDetailService.cancelRequest({
                queryId: this.innovationRequestDetailService.queryId,
                requestReference: undefined,
                userName: undefined,
                userPassword: undefined
            });

            if (cancelResponse.status !== 'Canceled')
                this.toastService.showError('TOAST.ERROR', `${cancelResponse.status}`, false);
            else {
                this.toastService.showSuccess('TOAST.SUCCESS', 'NEW_REQUEST.CANCEL', true);
                await this.innovationRequestDetailService.getRequestFromUrl(true);
            }
        } catch (e) {
            this.toastService.showError(`Error ${e.error.status} (${e.error.title})`, `${e.error.detail}`, false);
        } finally {
            this.innovationRequestDetailService.isLoading = false;
        }
    }

    confirmCancelOTP(event: Event): void {
        if (!event || !event.target) return;

        this.confirmationService.confirm({
            target: event.target,
            message: this.translate.instant('REQUEST_DETAIL.CANCEL_REQUEST_CONFIRMATION'),
            acceptLabel: this.translate.instant('BUTTON.YES'),
            rejectLabel: this.translate.instant('BUTTON.NO'),
            accept: () => {
                this.cancelOTP().catch(error => { console.error(error); });
            }
        });
    }

    verifyRequestCanBeReopened(): boolean {
        const request = this.innovationRequestDetailService.request;
        if (!request) return false;

        const status = request.personQuery.status;
        if (status.toLowerCase() !== 'processing') return false;

        const substatus = request.personQuery.substatus;
        return SUBSTATES_TO_REOPEN_BELENDER.includes(substatus);
    }

    isProd(): boolean {
        return environment.prodEnv;
    }

    hideContinueOTPButton(): boolean {
        const request = this.innovationRequestDetailService.request;
        if (!request) return true;

        const status = request.personQuery.status;
        if (STATES_TO_HIDE_CONTINUE_OTP_BUTTON.includes(status)) return true;

        const substatus = request.personQuery.substatus;
        return !SUBSTATES_TO_REOPEN_BELENDER.includes(substatus);
    }

    isLegalPersonRequest(): boolean {
        const personQuery = this.innovationRequestDetailService.request?.personQuery;
        if (!personQuery) return false;

        return personQuery.personType === '02' || personQuery.belenderCircuit === 6;
    }

    hideCancelOTPButton(): boolean {
        const request = this.innovationRequestDetailService.request;
        if (!request) return true;

        const status = request.personQuery.status;
        return STATES_TO_HIDE_CANCEL_OTP_BUTTON.includes(status);
    }

    hideDownloadZip(): boolean {
        const documents = this.innovationRequestDetailService.request.documents;
        return Object.keys(documents ?? {}).length === 0;
    }

    async downloadZIP(): Promise<void> {
        this.isDownloadZipButtonDisabled = true;
        let extraFiles: Record<string, string> | undefined = undefined;

        if (this.irpfTableService.isInit()) {
            const excelBlob = await this.irpfTableService.generateExcelBlob();
            const excelBuffer = await excelBlob.arrayBuffer();
            const base64Excel = this.utilsService.arrayBufferToBase64(excelBuffer);
            extraFiles = { ['IRPF.xlsx']: base64Excel };
        }

        const kpis = this.innovationRequestDetailService.kpis();
        if (kpis.length > 0) {
            const kpisBlob = await this.excelService.generateKpisExcelBlob(kpis);
            const kpisBuffer = await kpisBlob.arrayBuffer();
            const base64Kpis = this.utilsService.arrayBufferToBase64(kpisBuffer);
            extraFiles = { ...(extraFiles ?? {}), ['KPIs.xlsx']: base64Kpis };
        }



        const req: FilesRequest = {
            detail: this.innovationRequestDetailService.request,
            extraFiles: extraFiles,
            includeJSON: true
        };
        await this.utilsService.downloadZIP(req);
        this.isDownloadZipButtonDisabled = false;
    }
}
